/**
 * RIG embedded graph — renders the pure GraphModel as SVG, reproducing the
 * sealed visual grammar. No layout math here (that lives in ../layout.ts); this
 * is presentation + interaction only.
 *
 * Sealed compliance carried verbatim: PII never appears on the canvas; orange
 * is the participant-basis semantic only; bright teal is interaction-only (node
 * rings); the deep gradient appears only on the signal node.
 */

import type { KeyboardEvent } from "react";
import type { GEdge, GNode, GraphModel } from "../layout";
import { diamondPoints, hexPoints, truncateMiddle } from "../layout";
import { composeSignalNodeLabel, type SignalNodeLabel } from "../signalNodeLabel";

interface RigGraphProps {
  model: GraphModel;
  signal: SignalNodeLabel;
  highlightId: string | null;
  dim: boolean;
  onNode: (node: GNode) => void;
}

const RING_GLOW = { filter: "drop-shadow(0 0 6px rgba(80,208,198,.55))" } as const;

function edgeStroke(kind: GEdge["kind"]): {
  strokeWidth: number;
  dash?: string;
} {
  switch (kind) {
    case "participant":
    case "waypoint":
      return { strokeWidth: 1.75, dash: "6 4" };
    case "direct":
    case "delivery":
    default:
      return { strokeWidth: 2 };
  }
}

function Edge({ edge, delayMs }: { edge: GEdge; delayMs: number }): JSX.Element {
  const s = edgeStroke(edge.kind);
  // Dashed (participant/waypoint) edges keep their 6·4 pattern and reveal via a
  // staggered opacity fade (rig-edge-in). Solid (direct/delivery) edges get a
  // true staggered stroke draw-in (rig-edge-draw + pathLength normalisation) —
  // a stroke-dashoffset draw would fight a resting dash pattern, hence the split.
  if (s.dash) {
    return (
      <g className="rig-edge-in" style={{ animationDelay: `${delayMs}ms` }}>
        <path
          className="rig-edge"
          data-active={edge.active}
          d={edge.d}
          fill="none"
          stroke="var(--structural)"
          strokeWidth={s.strokeWidth}
          strokeDasharray={s.dash}
        />
      </g>
    );
  }
  return (
    <path
      className="rig-edge rig-edge-draw"
      data-active={edge.active}
      d={edge.d}
      fill="none"
      stroke="var(--structural)"
      strokeWidth={s.strokeWidth}
      pathLength={1}
      style={{ animationDelay: `${delayMs}ms` }}
    />
  );
}

function nodeHandlers(
  node: GNode,
  onNode: (n: GNode) => void,
): {
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent<SVGGElement>) => void;
  tabIndex: number;
  role: string;
} {
  return {
    onClick: () => onNode(node),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onNode(node);
      }
    },
    tabIndex: 0,
    role: "button",
  };
}

function ContactNode({
  node,
  selected,
  onNode,
}: {
  node: GNode;
  selected: boolean;
  onNode: (n: GNode) => void;
}): JSX.Element {
  const { x, y, r } = node;
  const halo =
    node.temp === "hot"
      ? "rgba(227,184,34,.16)"
      : node.temp === "warm"
        ? "rgba(227,184,34,.10)"
        : null;
  return (
    <g
      className={`rig-node${selected ? " rig-selected" : ""}`}
      data-id={node.id}
      data-active={node.active}
      aria-label={node.name}
      {...nodeHandlers(node, onNode)}
    >
      <title>{node.name}</title>
      <circle
        className="rig-ring"
        cx={x}
        cy={y}
        r={r + 6}
        fill="none"
        stroke="var(--interaction)"
        strokeWidth={2}
        style={RING_GLOW}
      />
      {halo ? <circle cx={x} cy={y} r={r + 12} fill={halo} /> : null}
      <circle
        className="rig-body"
        cx={x}
        cy={y}
        r={r}
        fill="url(#rig-nsphere)"
        stroke={node.basis === "participant" ? "var(--semantic)" : "rgba(8,87,94,.42)"}
        strokeWidth={1.5}
        filter="url(#rig-softshadow)"
      />
      <ellipse
        cx={x - r * 0.28}
        cy={y - r * 0.32}
        rx={r * 0.42}
        ry={r * 0.3}
        fill="rgba(255,255,255,.55)"
      />
      <text x={x} y={y + 5} textAnchor="middle" fontSize={15} fontWeight={600} fill="var(--ink)">
        {node.nodeInitials}
      </text>
      <g>
        <circle
          cx={x + 24}
          cy={y - 24}
          r={10}
          fill="rgba(255,255,255,.92)"
          stroke="var(--hairline)"
        />
        <text
          x={x + 24}
          y={y - 21}
          textAnchor="middle"
          className="rig-mono"
          fontSize={8}
          fill="var(--ink-55)"
        >
          {node.ownerInit}
        </text>
      </g>
      <text
        className="rig-nodename"
        x={x}
        y={y + r + 18}
        textAnchor="middle"
        fontSize={12}
        fontWeight={600}
        fill="var(--ink)"
      >
        {node.label ?? node.name}
      </text>
    </g>
  );
}

function DeliveryNode({
  node,
  selected,
  onNode,
}: {
  node: GNode;
  selected: boolean;
  onNode: (n: GNode) => void;
}): JSX.Element {
  const { x, y, r } = node;
  return (
    <g
      className={`rig-node${selected ? " rig-selected" : ""}`}
      data-id={node.id}
      data-active={node.active}
      aria-label={`Delivery: ${node.practice}`}
      {...nodeHandlers(node, onNode)}
    >
      <title>{node.snippet}</title>
      <polygon
        className="rig-ring"
        points={hexPoints(x, y, r + 4)}
        fill="none"
        stroke="var(--interaction)"
        strokeWidth={2}
        style={RING_GLOW}
      />
      <polygon
        className="rig-body"
        points={hexPoints(x, y, r)}
        fill="url(#rig-nsphere)"
        stroke="var(--hairline)"
        strokeWidth={1.25}
        filter="url(#rig-softshadow)"
      />
      <text
        x={x}
        y={y - 4}
        textAnchor="middle"
        className="rig-mono"
        fontSize={8}
        fill="var(--structural)"
      >
        {node.practiceAbbrev}
      </text>
      <text
        x={x}
        y={y + 11}
        textAnchor="middle"
        fontSize={15}
        fill={node.glyph === "✓" ? "var(--ink)" : "var(--ink-55)"}
      >
        {node.glyph}
      </text>
      {node.status === "Active" ? (
        <circle className="rig-graph-led" cx={x} cy={y + 22} r={3.5} fill="rgba(62,135,120,.55)" />
      ) : (
        <circle cx={x} cy={y + 22} r={3.5} fill="none" stroke="rgba(8,87,94,.5)" />
      )}
      {/* Hover snippet (3B.1): the full practice name surfaces above the hex on
          hover with a fade + slight translate. The <title> above keeps the full
          engagement snippet reachable natively (reduced-motion / a11y). */}
      {node.practice ? (
        <text
          className="rig-snippet"
          x={x}
          y={y - r - 8}
          textAnchor="middle"
          fontSize={9}
          fill="var(--structural)"
        >
          {truncateMiddle(node.practice, 22)}
        </text>
      ) : null}
    </g>
  );
}

function WaypointNode({
  node,
  selected,
  onNode,
}: {
  node: GNode;
  selected: boolean;
  onNode: (n: GNode) => void;
}): JSX.Element {
  const { x, y, r } = node;
  return (
    <g
      className={`rig-node${selected ? " rig-selected" : ""}`}
      data-id={node.id}
      data-active={node.active}
      aria-label="Consortium waypoint"
      {...nodeHandlers(node, onNode)}
    >
      <polygon
        className="rig-ring"
        points={diamondPoints(x, y, r + 6)}
        fill="none"
        stroke="var(--interaction)"
        strokeWidth={2}
        style={RING_GLOW}
      />
      <polygon
        className="rig-body"
        points={diamondPoints(x, y, r)}
        fill="url(#rig-nsphere)"
        stroke="var(--semantic)"
        strokeWidth={1.5}
        filter="url(#rig-softshadow)"
      />
      <ellipse cx={x - 6} cy={y - 8} rx={9} ry={6} fill="rgba(255,255,255,.5)" />
    </g>
  );
}

function ClusterNode({
  node,
  selected,
  onNode,
}: {
  node: GNode;
  selected: boolean;
  onNode: (n: GNode) => void;
}): JSX.Element {
  const { x, y, r } = node;
  const isDirect = node.id === "cluster-direct";
  const basisStroke = isDirect ? "rgba(8,87,94,.42)" : "var(--semantic)";
  return (
    <g
      className={`rig-node${selected ? " rig-selected" : ""}`}
      data-id={node.id}
      data-active={node.active}
      aria-label={`Cluster: ${node.count} more ${isDirect ? "direct contacts" : "participants"}`}
      {...nodeHandlers(node, onNode)}
    >
      <circle
        className="rig-ring"
        cx={x}
        cy={y}
        r={r + 6}
        fill="none"
        stroke="var(--interaction)"
        strokeWidth={2}
        style={RING_GLOW}
      />
      {/* One crisp sphere; a single faint offset disc is the depth hint — not
          literal stacked rings. */}
      <ellipse cx={x + 7} cy={y + 7} rx={r * 0.92} ry={r * 0.92} fill="url(#rig-nsphere)" opacity={0.4} />
      <circle
        className="rig-body"
        cx={x}
        cy={y}
        r={r}
        fill="url(#rig-nsphere)"
        stroke={basisStroke}
        strokeWidth={1.5}
        filter="url(#rig-softshadow)"
      />
      <ellipse cx={x - r * 0.28} cy={y - r * 0.32} rx={r * 0.4} ry={r * 0.28} fill="rgba(255,255,255,.5)" />
      <text x={x} y={y - 3} textAnchor="middle" fontSize={15} fontWeight={700} fill="var(--ink)">
        +{node.count}
      </text>
      <text x={x} y={y + 11} textAnchor="middle" className="rig-mono" fontSize={7.5} fill="var(--ink-55)">
        {isDirect ? "DIRECT" : "PARTICIPANT"}
      </text>
    </g>
  );
}

export function RigGraph({ model, signal, highlightId, dim, onNode }: RigGraphProps): JSX.Element {
  return (
    <svg
      className={`rig-svg${dim ? " rig-dim" : ""}`}
      viewBox={model.viewBox}
      role="img"
      aria-label="Relationship intelligence graph"
    >
      <defs>
        <linearGradient id="rig-deepgrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#073C46" />
          <stop offset="1" stopColor="#08575E" />
        </linearGradient>
        {/* Neumorphic sphere fill: light upper-left → soft lower-right. */}
        <radialGradient id="rig-nsphere" cx="36%" cy="32%" r="72%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="48%" stopColor="#eef4f3" />
          <stop offset="100%" stopColor="#d6e3e0" />
        </radialGradient>
        {/* Soft proximity-halo fade (replaces the dashed org boxes). */}
        <radialGradient id="rig-grouphalo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(8,87,94,0.07)" />
          <stop offset="70%" stopColor="rgba(8,87,94,0.03)" />
          <stop offset="100%" stopColor="rgba(8,87,94,0)" />
        </radialGradient>
        {/* Soft drop shadow giving the spheres their neumorphic lift. */}
        <filter id="rig-softshadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="4" dy="5" stdDeviation="5" floodColor="rgba(7,60,70,0.18)" />
        </filter>
      </defs>

      {model.groups.map((g) => (
        <g key={g.id}>
          <ellipse cx={g.cx} cy={g.cy} rx={g.rx} ry={g.ry} fill="url(#rig-grouphalo)" />
          {g.label ? (
            <text
              className="rig-grouplabel rig-mono"
              x={g.labelX}
              y={g.labelY}
              textAnchor="middle"
              fontSize={10}
              letterSpacing=".14em"
            >
              <title>{g.labelFull}</title>
              {g.label}
            </text>
          ) : null}
        </g>
      ))}

      {model.edges.map((edge, i) => (
        <Edge key={edge.id} edge={edge} delayMs={Math.min(i * 22, 300)} />
      ))}

      {model.deliveries.map((n) => (
        <DeliveryNode key={n.id} node={n} selected={n.id === highlightId} onNode={onNode} />
      ))}

      {model.waypoint ? (
        <WaypointNode node={model.waypoint} selected={model.waypoint.id === highlightId} onNode={onNode} />
      ) : null}
      {model.waypointLabel ? (
        <g>
          <title>{model.waypointLabel.full}</title>
          <text
            className="rig-grouplabel rig-mono"
            x={model.waypointLabel.x + model.waypointLabel.w / 2}
            y={model.waypointLabel.y + 14}
            textAnchor="middle"
            fontSize={9.5}
            letterSpacing=".08em"
          >
            {model.waypointLabel.text}
          </text>
        </g>
      ) : null}

      {model.clusters.map((c) => (
        <ClusterNode key={c.id} node={c} selected={c.id === highlightId} onNode={onNode} />
      ))}

      {model.participants.map((n) => (
        <ContactNode key={n.id} node={n} selected={n.id === highlightId} onNode={onNode} />
      ))}
      {model.directs.map((n) => (
        <ContactNode key={n.id} node={n} selected={n.id === highlightId} onNode={onNode} />
      ))}

      <SignalNode node={model.signal} label={signal} highlightId={highlightId} />
    </svg>
  );
}

function SignalNode({
  node,
  label,
  highlightId,
}: {
  node: GNode;
  label: SignalNodeLabel;
  highlightId: string | null;
}): JSX.Element {
  const w = 250;
  const h = 82;
  const bx = node.x - w / 2;
  const by = node.y - h / 2;
  const tx = bx + 20;
  // Fit the 250px signal node (text inset ~20px each side) so long/compound
  // titles middle-truncate cleanly instead of overrunning the plate.
  const line1 = truncateMiddle(label.line1, 25);
  const line2 = truncateMiddle(label.line2, 25);
  const renderedLine2 = line2 ? `— ${line2}` : "";
  // The signal node presents NO clickable affordance (Robert 2026-07-29):
  // clicking it has no payoff, so it carries no handlers, is not in the tab
  // order, and has no button role — only a passive hover ring (styled in CSS)
  // that reads as highlight, communicating edge ownership.
  return (
    <g
      className={`rig-node${node.id === highlightId ? " rig-selected" : ""}`}
      data-id="signal"
      data-active="true"
      aria-label="Signal"
    >
      <title>{composeSignalNodeLabel(label)}</title>
      <rect
        className="rig-ring"
        x={bx - 6}
        y={by - 6}
        width={w + 12}
        height={h + 12}
        rx={18}
        fill="none"
        stroke="var(--interaction)"
        strokeWidth={2}
        style={RING_GLOW}
      />
      <rect
        className="rig-body"
        x={bx}
        y={by}
        width={w}
        height={h}
        rx={15}
        fill="url(#rig-deepgrad)"
        filter="url(#rig-softshadow)"
      />
      <text x={tx} y={by + 27} className="rig-mono" fontSize={9.5} letterSpacing=".1em" fill="rgba(255,255,255,.62)">
        {label.eyebrow}
      </text>
      <text x={tx} y={by + 49} fontSize={13.5} fontWeight={600} fill="#fff">
        {line1}
      </text>
      <text x={tx} y={by + 68} fontSize={13.5} fontWeight={600} fill="#fff">
        {renderedLine2}
      </text>
    </g>
  );
}
